import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PageHeader,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Spinner,
  Modal,
  ModalFooter,
  useToast,
} from '@orientamos/ui'
import { Plus, FileStack, Search, Trash2, Eye, Edit, Copy, Wand2 } from 'lucide-react'
import {
  listTemplates,
  deleteTemplate,
  duplicateTemplate,
  type TemplateListItem,
  type TemplateFilters,
  type TemplateExtractionResult,
} from '@lib/api'
import { formatDate } from '@orientamos/shared'
import { AITemplateImportModal } from '@features/templates/components/AITemplateImportModal'

const typeOptions = [
  { value: '', label: 'Todos los tipos' },
  { value: 'residential', label: 'Residencial' },
  { value: 'commercial', label: 'Comercial' },
]

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'true', label: 'Activas' },
  { value: 'false', label: 'Inactivas' },
]

export default function TemplatesPage() {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [templates, setTemplates] = useState<TemplateListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })

  // Filters
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // AI import modal
  const [aiImportModalOpen, setAiImportModalOpen] = useState(false)

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<TemplateListItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadTemplates = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const filters: TemplateFilters = {
        page: pagination.page,
        limit: pagination.limit,
      }

      if (search) filters.search = search
      if (typeFilter) filters.type = typeFilter as 'residential' | 'commercial'
      if (statusFilter !== '') filters.isActive = statusFilter === 'true'

      const result = await listTemplates(filters)

      if (result.success) {
        setTemplates(result.data)
        setPagination(result.pagination)
      } else {
        setError('Error al cargar las plantillas')
      }
    } catch {
      setError('Error de conexion')
    } finally {
      setIsLoading(false)
    }
  }, [pagination.page, pagination.limit, search, typeFilter, statusFilter])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 1 }))
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const handleDelete = async () => {
    if (!templateToDelete) return

    setIsDeleting(true)
    try {
      const result = await deleteTemplate(templateToDelete.id)
      if (result.success) {
        // Update the template in the list as inactive
        setTemplates((prev) =>
          prev.map((t) => (t.id === templateToDelete.id ? { ...t, isActive: false } : t))
        )
        setDeleteModalOpen(false)
        setTemplateToDelete(null)
        addToast({
          variant: 'success',
          message: 'La plantilla ha sido desactivada exitosamente.',
        })
      }
    } catch (err) {
      console.error('Error deleting template:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo desactivar la plantilla.',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDuplicate = async (template: TemplateListItem) => {
    try {
      const result = await duplicateTemplate(template.id)
      if (result.success && result.data) {
        addToast({
          variant: 'success',
          message: `Plantilla duplicada. Nueva version ${result.data.version} creada.`,
        })
        loadTemplates()
      }
    } catch (err) {
      console.error('Error duplicating template:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo duplicar la plantilla.',
      })
    }
  }

  const handleAIImportApply = (result: TemplateExtractionResult) => {
    navigate('/templates/new', {
      state: { aiImport: result },
    })
  }

  const openDeleteModal = (template: TemplateListItem) => {
    setTemplateToDelete(template)
    setDeleteModalOpen(true)
  }

  return (
    <div>
      <PageHeader
        title="Plantillas de Contrato"
        description="Administra las plantillas para generar contratos"
        breadcrumbs={[{ label: 'Inicio', href: '/' }, { label: 'Plantillas' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setAiImportModalOpen(true)}>
              <Wand2 className="w-4 h-4 mr-1" />
              Importar con IA
            </Button>
            <Button onClick={() => navigate('/templates/new')}>
              <Plus className="w-4 h-4 mr-1" />
              Nueva Plantilla
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <Card className="mb-6" padding="sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Buscar por nombre o region..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="w-40">
            <Select
              options={typeOptions}
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            />
          </div>

          <div className="w-40">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            />
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            icon={<FileStack className="w-12 h-12" />}
            title="Error al cargar"
            description={error}
            action={<Button onClick={loadTemplates}>Reintentar</Button>}
          />
        </Card>
      ) : templates.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileStack className="w-12 h-12" />}
            title="Sin plantillas de contrato"
            description={
              search || typeFilter || statusFilter
                ? 'No se encontraron plantillas con los filtros seleccionados'
                : 'Las plantillas definen el formato y las secciones de los contratos. Crea una plantilla para comenzar a generar contratos.'
            }
            action={
              search || typeFilter || statusFilter ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('')
                    setTypeFilter('')
                    setStatusFilter('')
                  }}
                >
                  Limpiar filtros
                </Button>
              ) : (
                <Button onClick={() => navigate('/templates/new')}>
                  <Plus className="w-4 h-4 mr-1" />
                  Crear Primera Plantilla
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <Table>
            <TableHead>
              <TableRow hoverable={false}>
                <TableHeader>Nombre</TableHeader>
                <TableHeader>Tipo</TableHeader>
                <TableHeader>Region</TableHeader>
                <TableHeader>Version</TableHeader>
                <TableHeader>Estado</TableHeader>
                <TableHeader>Actualizado</TableHeader>
                <TableHeader className="w-40">Acciones</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <div className="font-medium text-neutral-900">{template.name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={template.type === 'residential' ? 'default' : 'secondary'}>
                      {template.type === 'residential' ? 'Residencial' : 'Comercial'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-neutral-600">{template.region}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">v{template.version}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={template.isActive ? 'success' : 'default'}>
                      {template.isActive ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-neutral-500">
                      {formatDate(template.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/templates/${template.id}`)}
                        title="Ver plantilla"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/templates/${template.id}/edit`)}
                        title="Editar plantilla"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(template)}
                        title="Duplicar plantilla"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      {template.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteModal(template)}
                          className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                          title="Desactivar plantilla"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200">
              <div className="text-sm text-neutral-500">
                Mostrando {(pagination.page - 1) * pagination.limit + 1} a{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                {pagination.total} plantillas
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* AI Template Import Modal */}
      <AITemplateImportModal
        open={aiImportModalOpen}
        onClose={() => setAiImportModalOpen(false)}
        onApply={handleAIImportApply}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Desactivar Plantilla"
        description="Esta accion desactivara la plantilla"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas desactivar la plantilla{' '}
          <span className="font-medium text-neutral-900">{templateToDelete?.name}</span>? La
          plantilla quedara inactiva pero no se eliminara.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
            Desactivar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
